import { useState } from 'react';
import { SUPPORT_EMAIL } from '../lib/lifecycle-notice.js';
import { api } from '../lib/api.js';
import { getIdTokenClaims } from '../lib/auth.js';
import SectionCard from '../components/SectionCard.jsx';

/**
 * Full-page message for a customer whose access the backend blocked (ENTRAID-332): HTTP 403 with the code
 * `subscription_ended` (subscription ended), `trust_role_missing` (onGuard trust stack lost) or `access_unavailable`
 * (the customer record could not be verified). Shown instead of the workbench, with a sign-out action and no
 * intermediate step. For `trust_role_missing` the admin can also open the trust stack restore link: the one thing the
 * backend still lets a blocked admin reach (trust-launch, a link only, nothing is changed).
 */
export const BLOCK_TEXT = Object.freeze({
  subscription_ended: {
    title: 'Subscription ended',
    message: 'Your onGuard for Entra ID subscription has ended. Your data stays in your AWS account. Contact IPC Global to resubscribe.',
  },
  trust_role_missing: {
    title: 'onGuard cannot reach your AWS account',
    message: 'The onGuard trust stack in your AWS account was removed or changed. Ask your AWS administrator to restore the onGuard trust stack, then contact IPC Global. Your data stays in your AWS account.',
  },
  access_unavailable: {
    title: 'Access could not be verified',
    message: 'Your access could not be verified. Contact IPC Global.',
  },
});

export default function EntitlementBlocked({ code, onSignOut }) {
  const text = BLOCK_TEXT[code] ?? BLOCK_TEXT.subscription_ended;
  const [restore, setRestore] = useState({ state: 'idle', url: null });

  async function showRestoreLink() {
    const tenant = getIdTokenClaims()?.active_tenant;
    if (!tenant) { setRestore({ state: 'error', url: null }); return; }
    setRestore({ state: 'loading', url: null });
    try {
      const res = await api.getTrustLaunch(tenant);
      setRestore(res?.launchUrl ? { state: 'ready', url: res.launchUrl } : { state: 'error', url: null });
    } catch {
      setRestore({ state: 'error', url: null });
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-ipc-surface px-4">
      <div className="w-full max-w-2xl" role="alert">
        <SectionCard as="div" ariaLabel={text.title}>
          <h1>{text.title}</h1>
          <p className="mt-2 text-ipc-muted">{text.message}</p>
          {code === 'trust_role_missing' && (
            <div className="mt-3">
              {restore.state === 'ready' ? (
                <p className="text-ipc-muted">
                  Send this link to your AWS administrator:{' '}
                  <a className="break-all text-ipc-light underline" href={restore.url} target="_blank" rel="noopener noreferrer">Restore the onGuard trust stack</a>
                </p>
              ) : (
                <button type="button" className="btn-secondary" onClick={showRestoreLink} disabled={restore.state === 'loading'}>
                  Show the trust stack restore link
                </button>
              )}
              {restore.state === 'error' && (
                <p className="mt-2 text-sm text-ipc-muted">The link is only available to the customer admin. Contact {SUPPORT_EMAIL}.</p>
              )}
            </div>
          )}
          <p className="mt-2 text-ipc-muted">
            Contact{' '}
            <a className="text-ipc-light underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
          <button type="button" className="btn-primary mt-4" onClick={onSignOut}>Sign out</button>
        </SectionCard>
      </div>
    </main>
  );
}
